import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import DeliveryDiningOutlinedIcon from '@mui/icons-material/DeliveryDiningOutlined';
import { getApiUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';
import PageMenuButton from '../components/PageMenuButton';

const API_URL = getApiUrl();
const ADDRESS_BOOK_ENABLED = true;

const clientesPageSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  overflowX: 'hidden',
  p: { xs: 1.5, md: 2 },
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
  '& .MuiPaper-root, & .MuiCard-root': {
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  '& .MuiFormControl-root, & .MuiTextField-root, & .MuiInputBase-root, & .MuiButton-root, & .MuiChip-root': {
    maxWidth: '100%',
  },
  '& .MuiInputBase-root': {
    minWidth: 0,
  },
  '& .MuiChip-label': {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

const theme = createTheme({
  palette: {
    primary: { main: '#6366f1' },
    secondary: { main: '#f59e0b' },
    background: { default: '#f8fafc', paper: '#ffffff' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 800,
      background: 'linear-gradient(135deg, #6366f1 0%, #f59e0b 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
  },
  shape: { borderRadius: 12 },
});

const normalizeWhatsapp = (value) => String(value || '').replace(/\D/g, '');

const formatWhatsapp = (value) => {
  const digits = normalizeWhatsapp(value);
  if (!digits) return '';
  if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return digits;
};

const emptyAddressForm = () => ({
  cep: '',
  street: '',
  number: '',
  reference: '',
  notes: '',
  neighborhood: '',
  city: '',
  state: '',
});

const buildCustomerAddress = (address = {}) => {
  const rua = String(address.street || '').trim();
  const numero = String(address.number || '').trim();
  const pontoReferencia = String(address.reference || '').trim();
  const observacoes = String(address.notes || '').trim();
  const bairro = String(address.neighborhood || '').trim();
  const cidade = String(address.city || '').trim();
  const estado = String(address.state || '').trim().toUpperCase();
  const cep = String(address.cep || '').replace(/\D/g, '').slice(0, 8);

  const line1 = [rua, numero && `N ${numero}`].filter(Boolean).join(', ');
  const line2 = [bairro, [cidade, estado].filter(Boolean).join(' - '), cep].filter(Boolean).join(', ');

  return [line1, line2, pontoReferencia && `Ponto de referência: ${pontoReferencia}`, observacoes && `Observações: ${observacoes}`]
    .filter(Boolean)
    .join(' | ');
};

const buildLegacyCustomerAddress = (address = {}) => {
  const rua = String(address.street || '').trim();
  const numero = String(address.number || '').trim();
  const pontoReferencia = String(address.reference || '').trim();
  const observacoes = String(address.notes || '').trim();
  const bairro = String(address.neighborhood || '').trim();
  const cidade = String(address.city || '').trim();

  const line1 = [rua, numero && `N ${numero}`].filter(Boolean).join(', ');
  const line2 = [bairro, cidade].filter(Boolean).join(' - ');
  return [line1, line2, pontoReferencia && `Ponto de referência: ${pontoReferencia}`, observacoes && `Observações: ${observacoes}`]
    .filter(Boolean)
    .join(' | ');
};

const parseCustomerAddress = (value) => {
  const raw = String(value || '').trim();
  const parsed = emptyAddressForm();
  if (!raw) return parsed;

  const parts = raw.split('|').map((part) => part.trim()).filter(Boolean);
  const line1 = parts[0] || '';
  const line2 = parts[1] || '';

  const numberMatch = line1.match(/^(.*?)(?:,\s*N\s*(.+))$/i);
  if (numberMatch) {
    parsed.street = numberMatch[1].trim();
    parsed.number = numberMatch[2].trim();
  } else {
    parsed.street = line1;
  }

  if (line2.includes(' - ')) {
    const [neighborhood, city] = line2.split(/\s-\s/, 2);
    parsed.neighborhood = (neighborhood || '').trim();
    parsed.city = (city || '').trim();
  } else if (line2) {
    parsed.neighborhood = line2;
  }

  parts.slice(2).forEach((part) => {
    if (/^Ponto de referência:/i.test(part)) {
      parsed.reference = part.replace(/^Ponto de referência:\s*/i, '').trim();
      return;
    }
    if (/^Observações:/i.test(part)) {
      parsed.notes = part.replace(/^Observações:\s*/i, '').trim();
      return;
    }
    if (!parsed.notes) parsed.notes = part;
  });

  return parsed;
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return String(iso);
  }
};

const sourceMeta = {
  ifood: { label: 'iFood', color: 'secondary', icon: <DeliveryDiningOutlinedIcon fontSize="small" /> },
  balcao: { label: 'Balcão', color: 'primary', icon: <StorefrontOutlinedIcon fontSize="small" /> },
  site: { label: 'Site', color: 'success', icon: <ReceiptLongOutlinedIcon fontSize="small" /> },
  brutos_zap: { label: 'WhatsApp', color: 'success', icon: <ReceiptLongOutlinedIcon fontSize="small" /> },
  pedidos10: { label: 'Pedidos10', color: 'warning', icon: <ReceiptLongOutlinedIcon fontSize="small" /> },
  '99food': { label: '99Food', color: 'warning', icon: <DeliveryDiningOutlinedIcon fontSize="small" /> },
  salao: { label: 'Salão', color: 'info', icon: <StorefrontOutlinedIcon fontSize="small" /> },
};

const extractApiError = async (response, fallbackMessage) => {
  try {
    const data = await response.json();
    return data?.error || `${fallbackMessage} (${response.status})`;
  } catch {
    try {
      const text = await response.text();
      return text ? `${fallbackMessage} (${response.status}): ${text}` : `${fallbackMessage} (${response.status})`;
    } catch {
      return `${fallbackMessage} (${response.status})`;
    }
  }
};

const Clientes = () => {
  const [query, setQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const customerLoadGenerationRef = useRef(0);

  const [dialog, setDialog] = useState({ open: false, mode: 'create', customer: null });
  const [form, setForm] = useState({ name: '', whatsapp: '', address: emptyAddressForm(), cpf: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [addressDialog, setAddressDialog] = useState({ open: false, addressId: null });
  const [addressForm, setAddressForm] = useState({ label: '', ...emptyAddressForm() });
  const [addressError, setAddressError] = useState(null);
  const [addressSaving, setAddressSaving] = useState(false);
  const [deleteAddressId, setDeleteAddressId] = useState(null);

  const [details, setDetails] = useState({ open: false, customer: null });
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState(null);
  const [sales, setSales] = useState([]);
  const [salesSummary, setSalesSummary] = useState({ count: 0, total: 0 });

  const loadCustomers = useCallback(async (q) => {
    const generation = ++customerLoadGenerationRef.current;
    setLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/customers?q=${encodeURIComponent(q || '')}`);
      if (!response.ok) throw new Error(await extractApiError(response, 'Falha ao carregar clientes'));
      const data = await response.json();
      if (generation !== customerLoadGenerationRef.current) return;
      setCustomers(data.customers || []);
      setError(null);
    } catch (err) {
      if (generation !== customerLoadGenerationRef.current) return;
      setError(err.message);
      setCustomers([]);
    } finally {
      if (generation === customerLoadGenerationRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => loadCustomers(query), 250);
    return () => clearTimeout(t);
  }, [loadCustomers, query]);

  const filteredCustomers = useMemo(() => {
    if (sourceFilter === 'all') return customers;
    return customers.filter((c) => Array.isArray(c.sources) && c.sources.includes(sourceFilter));
  }, [customers, sourceFilter]);

  const openCreate = useCallback(() => {
    setFormError(null);
    setForm({ name: '', whatsapp: '', address: emptyAddressForm(), cpf: '' });
    setDialog({ open: true, mode: 'create', customer: null });
  }, []);

  const openEdit = useCallback((customer) => {
    setFormError(null);
    setForm({
      name: customer.name || '',
      whatsapp: customer.whatsapp || '',
      address: customer.addressDetails
        ? { ...emptyAddressForm(), ...customer.addressDetails }
        : parseCustomerAddress(customer.address),
      cpf: customer.cpf || '',
    });
    setDialog({ open: true, mode: 'edit', customer });
  }, []);

  const openDetails = useCallback((customer) => {
    setSales([]);
    setSalesError(null);
    setSalesSummary({ count: 0, total: 0 });
    setDetails({ open: true, customer });
  }, []);

  const applyAddressCustomer = useCallback((customer) => {
    if (!customer?.id) return;
    setCustomers((current) => current.map((entry) => entry.id === customer.id ? customer : entry));
    setDetails((current) => current.customer?.id === customer.id ? { ...current, customer } : current);
  }, []);

  const openAddressDialog = useCallback((entry = null) => {
    setAddressError(null);
    setAddressForm({ label: entry?.label || '', ...emptyAddressForm(), ...(entry?.addressDetails || {}) });
    setAddressDialog({ open: true, addressId: entry?.id || null });
  }, []);

  const mutateAddress = useCallback(async (method, path, payload = null) => {
    const customerId = details.customer?.id;
    if (!customerId) return null;
    customerLoadGenerationRef.current += 1;
    setLoading(false);
    const response = await apiFetch(`${API_URL}/api/customers/${encodeURIComponent(customerId)}/addresses${path}`, {
      method,
      headers: { 'Content-Type': 'application/json' },
      ...(payload ? { body: JSON.stringify(payload) } : null),
    });
    if (!response.ok) throw new Error(await extractApiError(response, 'Falha ao atualizar endereço'));
    const data = await response.json();
    if (!data.customer?.id) throw new Error('Cadastro atualizado sem confirmação do cliente.');
    applyAddressCustomer(data.customer);
    return data.customer;
  }, [applyAddressCustomer, details.customer?.id]);

  const saveAddress = useCallback(async () => {
    const detailsValue = { ...emptyAddressForm(), ...addressForm, cep: String(addressForm.cep || '').replace(/\D/g, '') };
    const hasRequired = ['street', 'number', 'neighborhood', 'city', 'state']
      .every((field) => String(detailsValue[field] || '').trim());
    if (!hasRequired || !/^[A-Za-z]{2}$/.test(detailsValue.state) ||
      (detailsValue.cep && !/^\d{8}$/.test(detailsValue.cep))) {
      setAddressError('Complete rua, número, bairro, cidade e UF. O CEP, se informado, deve ter 8 dígitos.');
      return;
    }
    setAddressSaving(true);
    setAddressError(null);
    try {
      const addressDetails = { ...detailsValue };
      delete addressDetails.label;
      await mutateAddress(addressDialog.addressId ? 'PUT' : 'POST',
        addressDialog.addressId ? `/${encodeURIComponent(addressDialog.addressId)}` : '', {
          label: addressForm.label.trim(),
          address: buildCustomerAddress(addressDetails),
          addressDetails,
        });
      setAddressDialog({ open: false, addressId: null });
    } catch (error) {
      setAddressError(error.message);
    } finally {
      setAddressSaving(false);
    }
  }, [addressDialog.addressId, addressForm, mutateAddress]);

  const updateAddressAction = useCallback(async (action, addressId) => {
    setAddressSaving(true);
    setAddressError(null);
    try {
      await mutateAddress(action === 'default' ? 'PUT' : 'DELETE',
        `/${encodeURIComponent(addressId)}${action === 'default' ? '/default' : ''}`);
      setDeleteAddressId(null);
    } catch (error) {
      setAddressError(error.message);
    } finally {
      setAddressSaving(false);
    }
  }, [mutateAddress]);

  const closeDetails = useCallback(() => {
    setDetails({ open: false, customer: null });
    setSales([]);
    setSalesError(null);
    setSalesSummary({ count: 0, total: 0 });
  }, []);

  const loadSales = useCallback(async (customerId) => {
    if (!customerId) return;
    setSalesLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/customers/${customerId}/sales`);
      if (!response.ok) throw new Error('Falha ao carregar histórico de vendas');
      const data = await response.json();
      setSales(Array.isArray(data.sales) ? data.sales : []);
      setSalesSummary(data.summary || { count: 0, total: 0 });
      setSalesError(null);
    } catch (err) {
      setSalesError(err.message);
      setSales([]);
      setSalesSummary({ count: 0, total: 0 });
    } finally {
      setSalesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!details.open || !details.customer?.id) return;
    loadSales(details.customer.id);
  }, [details.customer?.id, details.open, loadSales]);

  const validate = useCallback(() => {
    const name = form.name.trim();
    const whatsapp = normalizeWhatsapp(form.whatsapp);
    if (!name) return 'Nome é obrigatório.';
    if (!whatsapp) return 'WhatsApp é obrigatório.';
    if (dialog.mode === 'create' && form.address.cep && !/^\d{8}$/.test(String(form.address.cep))) return 'O CEP, se informado, deve ter 8 dígitos.';
    if (dialog.mode === 'create' && form.address.state && !/^[A-Za-z]{2}$/.test(String(form.address.state))) return 'Informe uma UF válida.';
    return null;
  }, [dialog.mode, form]);

  const save = useCallback(async () => {
    setFormError(null);
    const validation = validate();
    if (validation) {
      setFormError(validation);
      return;
    }

    setSaving(true);
    try {
      const hasManagedAddresses = dialog.mode === 'edit' && Array.isArray(dialog.customer?.addresses);
      const initialLegacyAddress = dialog.customer?.addressDetails
        ? { ...emptyAddressForm(), ...dialog.customer.addressDetails }
        : parseCustomerAddress(dialog.customer?.address);
      const legacyAddressChanged = dialog.mode === 'edit' && Object.keys(emptyAddressForm())
        .some((field) => String(form.address[field] || '') !== String(initialLegacyAddress[field] || ''));
      const payload = {
        name: form.name.trim(),
        whatsapp: normalizeWhatsapp(form.whatsapp),
        cpf: form.cpf.trim(),
        ...(dialog.mode === 'create' ? {
          address: ADDRESS_BOOK_ENABLED
            ? buildCustomerAddress(form.address)
            : buildLegacyCustomerAddress(form.address),
          ...(ADDRESS_BOOK_ENABLED ? {
            addressDetails: Object.values(form.address).some((value) => String(value || '').trim()) ? form.address : null,
          } : null),
        } : !hasManagedAddresses ? {
          address: legacyAddressChanged
            ? (dialog.customer?.addressDetails ? buildCustomerAddress(form.address) : buildLegacyCustomerAddress(form.address))
            : dialog.customer?.address || '',
        } : null),
      };

      const url =
        dialog.mode === 'edit'
          ? `${API_URL}/api/customers/${dialog.customer.id}`
          : `${API_URL}/api/customers`;
      const method = dialog.mode === 'edit' ? 'PUT' : 'POST';

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await extractApiError(response, 'Falha ao salvar cliente'));
      }

      const data = await response.json();
      if (data.customer?.id) applyAddressCustomer(data.customer);

      setDialog({ open: false, mode: 'create', customer: null });
      await loadCustomers(query);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  }, [applyAddressCustomer, dialog, form, loadCustomers, query, validate]);

  const stats = useMemo(() => {
    const totalCustomers = filteredCustomers.length;
    const totals = filteredCustomers.reduce(
      (acc, c) => {
        acc.purchases += Number(c.purchaseCount || 0) || 0;
        acc.spent += Number(c.totalSpent || 0) || 0;
        (c.sources || []).forEach((s) => {
          acc.sources[s] = (acc.sources[s] || 0) + 1;
        });
        return acc;
      },
      { purchases: 0, spent: 0, sources: {} }
    );

    return {
      totalCustomers,
      totalPurchases: totals.purchases,
      totalSpent: totals.spent,
      sources: totals.sources,
    };
  }, [filteredCustomers]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={clientesPageSx}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} gap={1.5}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <PageMenuButton />
              <Typography variant="h4">Clientes</Typography>
            </Stack>
            <Typography color="text.secondary">
              Cadastre clientes e acompanhe compras por canal. Dados protegidos dos marketplaces não são usados para identificar clientes.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<PersonAddAltOutlinedIcon />} onClick={openCreate} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Novo cliente
          </Button>
        </Stack>

        <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
          <Card variant="outlined" sx={{ borderRadius: 3, borderColor: '#e2e8f0' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Clientes
              </Typography>
              <Typography variant="h5" fontWeight={900}>
                {stats.totalCustomers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Com filtro: {sourceFilter === 'all' ? 'todos' : sourceMeta[sourceFilter]?.label || sourceFilter}
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ borderRadius: 3, borderColor: '#e2e8f0' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Compras registradas
              </Typography>
              <Typography variant="h5" fontWeight={900}>
                {stats.totalPurchases}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Atualiza automaticamente em pedidos finalizados
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ borderRadius: 3, borderColor: '#e2e8f0' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Total movimentado
              </Typography>
              <Typography variant="h5" fontWeight={900}>
                {formatCurrency(stats.totalSpent)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Soma das compras associadas aos clientes
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Paper
          sx={{
            p: 2,
            mt: 2,
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }} sx={{ minWidth: 0 }}>
            <TextField
              fullWidth
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nome, WhatsApp ou endereço..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ width: { xs: '100%', md: 200 }, flex: { md: '0 0 200px' } }}>
              <InputLabel id="clientes-source-filter-label">Origem</InputLabel>
              <Select
                labelId="clientes-source-filter-label"
                value={sourceFilter}
                label="Origem"
                onChange={(e) => setSourceFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="balcao">Balcão</MenuItem>
                <MenuItem value="ifood">iFood</MenuItem>
                <MenuItem value="site">Site</MenuItem>
                <MenuItem value="brutos_zap">WhatsApp</MenuItem>
                <MenuItem value="pedidos10">Pedidos10</MenuItem>
                <MenuItem value="99food">99Food</MenuItem>
              </Select>
            </FormControl>
            <Chip label={`${filteredCustomers.length} cliente(s)`} />
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : filteredCustomers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary">
                Nenhum cliente encontrado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cadastre um cliente para usar nas vendas e registrar histórico.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 1.5 }}>
              {filteredCustomers.map((customer) => (
                <Paper
                  key={customer.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    borderColor: '#e2e8f0',
                    cursor: 'pointer',
                    transition: 'transform 120ms ease, box-shadow 120ms ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 10px 30px rgba(2, 6, 23, 0.08)',
                    },
                  }}
                  onClick={() => openDetails(customer)}
                >
                  <Stack direction="row" gap={1.5} alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main', fontWeight: 900 }}>
                      <PhoneIphoneOutlinedIcon />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={900} noWrap>
                        {customer.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {formatWhatsapp(customer.whatsapp) || 'Sem telefone'}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditOutlinedIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(customer);
                      }}
                    >
                      Editar
                    </Button>
                  </Stack>

                  <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
                    {(customer.sources || []).length > 0 ? (
                      (customer.sources || []).map((src) => {
                        const meta = sourceMeta[src] || { label: src, color: 'default', icon: null };
                        return (
                          <Chip
                            key={src}
                            size="small"
                            color={meta.color}
                            icon={meta.icon}
                            label={meta.label}
                            sx={{ fontWeight: 700 }}
                          />
                        );
                      })
                    ) : (
                      <Chip size="small" label="Sem origem" />
                    )}
                    <Chip size="small" label={`${Number(customer.purchaseCount || 0)} compra(s)`} />
                    <Chip size="small" label={formatCurrency(customer.totalSpent || 0)} />
                    {customer.lastPurchaseAt && (
                      <Chip size="small" variant="outlined" label={`Última: ${formatDateTime(customer.lastPurchaseAt)}`} />
                    )}
                  </Stack>

                  {(customer.address || customer.cpf || (customer.aliases || []).length > 0) && (
                    <Box sx={{ mt: 1.25 }}>
                      {customer.address && (
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                          {customer.address}
                        </Typography>
                      )}
                      {customer.cpf && (
                        <Typography variant="caption" color="text.secondary">
                          CPF: {customer.cpf}
                        </Typography>
                      )}
                      {(customer.aliases || []).length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Variações de nome: {(customer.aliases || []).slice(0, 3).join(', ')}
                          {(customer.aliases || []).length > 3 ? '…' : ''}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </Paper>

        <Drawer
          anchor="right"
          open={details.open}
          onClose={closeDetails}
          PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden', p: 2, background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' } }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-start' }} gap={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={900} noWrap>
                {details.customer?.name || 'Cliente'}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {formatWhatsapp(details.customer?.whatsapp) || 'Sem telefone'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
              onClick={() => {
                if (details.customer) openEdit(details.customer);
              }}
            >
              Editar
            </Button>
          </Stack>

          <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
            {(details.customer?.sources || []).map((src) => {
              const meta = sourceMeta[src] || { label: src, color: 'default', icon: null };
              return (
                <Chip
                  key={src}
                  size="small"
                  color={meta.color}
                  icon={meta.icon}
                  label={meta.label}
                  sx={{ fontWeight: 700 }}
                />
              );
            })}
            <Chip size="small" label={`${Number(details.customer?.purchaseCount || 0)} compra(s)`} />
            <Chip size="small" label={formatCurrency(details.customer?.totalSpent || 0)} />
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
            Dados
          </Typography>
          <Stack spacing={0.75}>
            <Typography variant="body2" color="text.secondary">
              {Array.isArray(details.customer?.addresses) ? 'Endereço principal' : 'Endereço'}: {Array.isArray(details.customer?.addresses)
                ? details.customer.addresses.find((entry) => entry.isDefault)?.address || details.customer.addresses[0]?.address || '—'
                : details.customer?.address || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              CPF: {details.customer?.cpf ? details.customer.cpf : '—'}
            </Typography>
            {(details.customer?.aliases || []).length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Variações de nome: {(details.customer.aliases || []).join(', ')}
              </Typography>
            )}
          </Stack>

          <Divider sx={{ my: 2 }} />

          {Array.isArray(details.customer?.addresses) && <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={900}>Endereços salvos</Typography>
            <Button size="small" onClick={() => openAddressDialog()} disabled={addressSaving || (details.customer?.addresses || []).length >= 10}>
              Novo endereço
            </Button>
          </Stack>
          {addressError && !addressDialog.open && <Alert severity="error" sx={{ mb: 1 }}>{addressError}</Alert>}
          {(details.customer?.addresses || []).length ? (
            <Stack spacing={1}>
              {details.customer.addresses.map((entry) => (
                <Paper key={entry.id} variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                  <Stack spacing={0.5}>
                    <Typography variant="body2" fontWeight={800}>
                      {entry.label || 'Endereço'}{entry.isDefault ? ' · Padrão' : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{entry.address}</Typography>
                    {!entry.addressDetails && (
                      <Typography variant="caption" color="warning.main">Endereço antigo: complete os campos antes de usar para entrega.</Typography>
                    )}
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      <Button size="small" aria-label={`Editar endereço ${entry.label || entry.address}`} onClick={() => openAddressDialog(entry)}>Editar</Button>
                      {!entry.isDefault && (
                        <Button size="small" onClick={() => updateAddressAction('default', entry.id)} disabled={addressSaving}>Definir padrão</Button>
                      )}
                      <Button size="small" color="error" aria-label={`Excluir endereço ${entry.label || entry.address}`} onClick={() => setDeleteAddressId(entry.id)} disabled={addressSaving}>Excluir</Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">Nenhum endereço salvo.</Typography>
          )}
          </>}

          <Divider sx={{ my: 2 }} />

          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} sx={{ mb: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={900}>
              Histórico de vendas
            </Typography>
            <Chip size="small" label={`${Number(salesSummary.count || 0)} venda(s) · ${salesSummary.unknownAmountCount ? `${Number(salesSummary.count) > Number(salesSummary.unknownAmountCount) ? `${formatCurrency(salesSummary.total || 0)} conhecidos · ` : ''}${salesSummary.unknownAmountCount} com total não informado` : formatCurrency(salesSummary.total || 0)}`} sx={{ maxWidth: '100%' }} />
          </Stack>

          {salesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {salesError}
            </Alert>
          )}

          {salesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : sales.length === 0 ? (
            <Box sx={{ py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Nenhuma venda encontrada para este cliente.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Dica: pedidos do balcão registram compra ao criar; iFood registra ao finalizar.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {sales.slice(0, 30).map((sale) => {
                const meta = sourceMeta[sale.source] || { label: sale.source, color: 'default', icon: null };
                return (
                  <Paper key={sale.orderId} variant="outlined" sx={{ p: 1.25, borderRadius: 2, borderColor: '#e2e8f0' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                      <Stack spacing={0.1} sx={{ minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                          <Chip size="small" color={meta.color} icon={meta.icon} label={meta.label} />
                          <Typography variant="body2" fontWeight={900} noWrap>
                            {sale.displayId || sale.orderId}
                          </Typography>
                          {sale.status && <Chip size="small" variant="outlined" label={sale.status} />}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {formatDateTime(sale.finishedAt || sale.createdAt)}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" fontWeight={900}>
                        {sale.amountKnown === false ? 'Total não informado pela 99Food' : formatCurrency(sale.total || 0)}
                      </Typography>
                    </Stack>
                  </Paper>
                );
              })}
              {sales.length > 30 && (
                <Typography variant="caption" color="text.secondary">
                  Mostrando as últimas 30 vendas.
                </Typography>
              )}
            </Stack>
          )}
        </Drawer>

        <Dialog open={addressDialog.open} onClose={addressSaving ? undefined : () => setAddressDialog({ open: false, addressId: null })} maxWidth="sm" fullWidth>
          <DialogTitle>{addressDialog.addressId ? 'Editar endereço' : 'Novo endereço'}</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {addressError && <Alert severity="error">{addressError}</Alert>}
              <TextField label="Rótulo (opcional)" value={addressForm.label} onChange={(event) => setAddressForm((current) => ({ ...current, label: event.target.value }))} inputProps={{ maxLength: 80 }} placeholder="Casa, Trabalho..." />
              <TextField label="CEP (opcional)" value={addressForm.cep} onChange={(event) => setAddressForm((current) => ({ ...current, cep: event.target.value.replace(/\D/g, '').slice(0, 8) }))} inputProps={{ inputMode: 'numeric', maxLength: 8 }} />
              {[
                ['street', 'Rua'], ['number', 'Número'], ['neighborhood', 'Bairro'],
                ['city', 'Cidade'], ['state', 'UF'], ['reference', 'Ponto de Referência'], ['notes', 'Observações'],
              ].map(([field, label]) => (
                <TextField
                  key={field}
                  label={label}
                  value={addressForm[field]}
                  onChange={(event) => setAddressForm((current) => ({ ...current, [field]: field === 'state' ? event.target.value.toUpperCase().slice(0, 2) : event.target.value }))}
                  required={['street', 'number', 'neighborhood', 'city', 'state'].includes(field)}
                  multiline={field === 'notes'}
                  minRows={field === 'notes' ? 2 : undefined}
                />
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddressDialog({ open: false, addressId: null })} disabled={addressSaving}>Cancelar</Button>
            <Button variant="contained" onClick={saveAddress} disabled={addressSaving}>{addressSaving ? 'Salvando...' : 'Salvar endereço'}</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(deleteAddressId)} onClose={addressSaving ? undefined : () => setDeleteAddressId(null)}>
          <DialogTitle>Excluir endereço?</DialogTitle>
          <DialogContent><Typography>O endereço será removido do cadastro do cliente. Pedidos anteriores não serão alterados.</Typography></DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteAddressId(null)} disabled={addressSaving}>Cancelar</Button>
            <Button color="error" variant="contained" onClick={() => updateAddressAction('delete', deleteAddressId)} disabled={addressSaving}>Excluir endereço</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={dialog.open} onClose={() => setDialog({ open: false, mode: 'create', customer: null })} maxWidth="sm" fullWidth>
          <DialogTitle>{dialog.mode === 'edit' ? 'Editar cliente' : 'Cadastrar cliente'}</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {formError && <Alert severity="error">{formError}</Alert>}
              <TextField
                label="Nome (obrigatório)"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                required
                autoFocus
              />
              <TextField
                label="WhatsApp (obrigatório)"
                value={form.whatsapp}
                onChange={(e) => setForm((prev) => ({ ...prev, whatsapp: e.target.value }))}
                required
              />
              {(dialog.mode === 'create' || !Array.isArray(dialog.customer?.addresses)) && (
                <>
              {dialog.mode === 'create' && ADDRESS_BOOK_ENABLED && <TextField
                label="CEP (opcional)"
                value={form.address.cep}
                onChange={(e) => setForm((prev) => ({ ...prev, address: { ...prev.address, cep: e.target.value.replace(/\D/g, '').slice(0, 8) } }))}
                inputProps={{ inputMode: 'numeric', maxLength: 8 }}
              />}
              <TextField
                label="Rua"
                value={form.address.street}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    address: { ...prev.address, street: e.target.value },
                  }))
                }
              />
              <TextField
                label="Número"
                value={form.address.number}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    address: { ...prev.address, number: e.target.value },
                  }))
                }
              />
              <TextField
                label="Ponto de Referência"
                value={form.address.reference}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    address: { ...prev.address, reference: e.target.value },
                  }))
                }
              />
              <TextField
                label="Observações"
                value={form.address.notes}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    address: { ...prev.address, notes: e.target.value },
                  }))
                }
                multiline
                minRows={2}
              />
              <TextField
                label="Bairro"
                value={form.address.neighborhood}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    address: { ...prev.address, neighborhood: e.target.value },
                  }))
                }
              />
              <TextField
                label="Cidade"
                value={form.address.city}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    address: { ...prev.address, city: e.target.value },
                  }))
                }
              />
              {dialog.mode === 'create' && ADDRESS_BOOK_ENABLED && <TextField
                label="UF"
                value={form.address.state}
                onChange={(e) => setForm((prev) => ({ ...prev, address: { ...prev.address, state: e.target.value.toUpperCase().slice(0, 2) } }))}
                inputProps={{ maxLength: 2 }}
              />}
                </>
              )}
              <TextField
                label="CPF"
                value={form.cpf}
                onChange={(e) => setForm((prev) => ({ ...prev, cpf: e.target.value }))}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialog({ open: false, mode: 'create', customer: null })} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default Clientes;
